/**
 * i18n — the demo's lightweight HU/EN layer (no external library): a React
 * context over the typed dictionary in ./messages, with localStorage
 * persistence ('walletDemo.lang', default 'hu').
 *
 * Components read copy via `useT()`. The context carries a WORKING default
 * (hu translator + no-op setter), so every component test that mounts a page
 * without <I18nProvider> keeps rendering the exact Hungarian copy it always
 * asserted — the provider is only required for switching.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { LANG_STORAGE_KEY, tHu, translateFor, type Lang, type Translate } from './messages';

export {
  LANG_STORAGE_KEY,
  messages,
  tHu,
  translateFor,
  type Lang,
  type MessageKey,
  type Translate,
} from './messages';

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translate;
}

/** Guarded read — jsdom/browser both have localStorage, but stay defensive. */
export function readStoredLang(): Lang {
  try {
    return window.localStorage.getItem(LANG_STORAGE_KEY) === 'en' ? 'en' : 'hu';
  } catch {
    return 'hu';
  }
}

const I18nContext = createContext<I18nContextValue>({
  lang: 'hu',
  setLang: () => {},
  t: tHu,
});

export function I18nProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [lang, setLangState] = useState<Lang>(readStoredLang);
  const setLang = useCallback((next: Lang): void => {
    setLangState(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort — the in-memory switch still applies.
    }
  }, []);
  const value = useMemo<I18nContextValue>(
    () => ({ lang, setLang, t: translateFor(lang) }),
    [lang, setLang],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Language + setter + translator (the toggle and locale-aware formatters). */
export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

/** Just the translator — the common case. */
export function useT(): Translate {
  return useContext(I18nContext).t;
}

/**
 * The HU/EN header toggle — rendered in the Layout header and standalone on
 * the auth pages (Login/Verify mount outside the Layout).
 */
export function LangToggle(): JSX.Element {
  const { lang, setLang, t } = useI18n();
  const btnClass = (active: boolean): string =>
    `px-1.5 py-0.5 rounded text-xs font-semibold transition ${
      active ? 'bg-brand-700 text-white' : 'text-slate-500 hover:text-slate-900'
    }`;
  return (
    <div
      role="group"
      aria-label={t('lang.switchAria')}
      className="flex items-center gap-0.5 rounded-md ring-1 ring-slate-200 p-0.5"
    >
      <button
        type="button"
        aria-pressed={lang === 'hu'}
        onClick={() => setLang('hu')}
        className={btnClass(lang === 'hu')}
      >
        HU
      </button>
      <button
        type="button"
        aria-pressed={lang === 'en'}
        onClick={() => setLang('en')}
        className={btnClass(lang === 'en')}
      >
        EN
      </button>
    </div>
  );
}
